import { bool, fields, flt, obj, str } from "../fields";
import { make_schema } from "../utils/schema/make_schema";

/**
 * 帧的 3D 模型视觉
 *
 * 模式 A：帧 = 3D 姿态关键帧
 *   - pose: 骨骼姿态（相邻帧 pose 之间做插值）
 *
 * 模式 B：帧 = 动画片段触发器
 *   - anim: glTF 动画片段名
 *
 * 帧存在 model 时优先用模型渲染，否则回退到 pic/pics（2D 精灵）
 *
 * @export
 * @interface IFrameModel
 */
export interface IFrameModelPose {
  /** 骨骼名（可选，缺省时按骨骼遍历顺序应用） */
  bones?: string[];
  /** 每个骨骼的 position，展平为 x,y,z 序列 */
  pos?: number[];
  /** 每个骨骼的 rotation(四元数)，展平为 x,y,z,w 序列 */
  rot?: number[];
  /** 每个骨骼的 scale，展平为 x,y,z 序列 */
  scl?: number[];
}
export interface IFrameModel {
  /** base.models 里的模型 ID */
  id: string;
  /** 模式 A：骨骼姿态（可选，缺省则用动画片段） */
  pose?: IFrameModelPose;
  /** 模式 B：glTF 动画片段名 */
  anim?: string;
  /**
   * 模式 B · 片段定位：该帧在 anim 片段内要定位到的起始时间（秒，缺省 0）
   *
   * 用于把“帧序列”映射到“长动画片段内部”：
   * 相邻帧（frame.next 且同模型同片段）用各自的 seek 划分片段区间，
   * 当前帧按帧内进度 t∈[0,1]（由 wait 推导）线性推进到 [seek, 下一帧 seek)。
   * 缺省（undefined）时走旧的连续播放（mixer 累计）。
   */
  seek?: number;
  seek_end?: number;
  hold_last?: boolean;
  /** 动画是否循环 */
  loop?: boolean;
  /** 动画速度倍率（配合 wait 控制节奏） */
  time_scale?: number;
  /**
   * 整模型绕 Z 轴旋转角（弧度，缺省 0），rotation.z 的简写，类似 2D pic 的 rad
   * @deprecated 用 rotation.z
   */
  rad?: number;
  /**
   * 任意旋转（欧拉角，弧度，缺省 0；three.js 默认 XYZ 顺序）。
   * 与 base 模型 rotation 组合：先应用 base 旋转，帧旋转作用于模型自身坐标系。
   * 兼容旧数据：rotation.z 缺省时回退到 rad。
   */
  rotation?: { x?: number; y?: number; z?: number };
  /** 帧级缩放系数（x,y,z，缺省 1）；与 base 模型 scale 同时存在时逐轴相乘 */
  scale?: { x?: number; y?: number; z?: number };
  /** 帧级平移偏移（x,y,z，缺省 0）；与 base 模型 offset 逐轴相加 */
  offset?: { x?: number; y?: number; z?: number };
}
export function frame_model_new(): IFrameModel {
  return { id: "" }
}
const frame_model_pose_fields = fields<IFrameModelPose>({
  bones: str('骨骼名', { array: true }),
  pos: flt('位置', { array: true }),
  rot: flt('旋转(四元数)', { array: true }),
  scl: flt('缩放', { array: true }),
});
const vec3_scale_fields = fields<{ x?: number; y?: number; z?: number }>({
  x: flt('X'),
  y: flt('Y'),
  z: flt('Z'),
});
const vec3_offset_fields = fields<{ x?: number; y?: number; z?: number }>({
  x: flt('X'),
  y: flt('Y'),
  z: flt('Z'),
});
const vec3_rotation_fields = fields<{ x?: number; y?: number; z?: number }>({
  x: flt('X'),
  y: flt('Y'),
  z: flt('Z'),
});
export const frame_model_fields = fields<IFrameModel>({
  id: str('模型ID'),
  pose: obj('姿态', { nullable: true, fields: frame_model_pose_fields }),
  anim: str('动画片段', { nullable: true }),
  seek: flt('片段定位(秒)', { nullable: true }),
  seek_end: flt('片段定位终点(秒)', { nullable: true }),
  hold_last: bool('播完保持最后一帧', { nullable: true }),
  loop: bool('循环', { nullable: true }),
  time_scale: flt('速度倍率', { nullable: true }),
  rad: flt('Z轴旋转(弧度)', { nullable: true }),
  rotation: obj('旋转(弧度 x,y,z)', { nullable: true, fields: vec3_rotation_fields }),
  scale: obj('缩放', { nullable: true, fields: vec3_scale_fields }),
  offset: obj('偏移', { nullable: true, fields: vec3_offset_fields }),
});

export const Schema_IFrameModel = make_schema<IFrameModel>({
  key: 'IFrameModel',
  type: 'object',
  properties: {
    id: { type: "string", description: "模型ID" },
    pose: { type: "object", nullable: true, description: "骨骼姿态" },
    anim: { type: "string", nullable: true, description: "动画片段名" },
    seek: { type: "number", nullable: true, description: "片段内定位起始时间(秒)" },
    seek_end: { type: "number", nullable: true, description: "片段内定位终点时间(秒)" },
    hold_last: { type: "boolean", nullable: true, description: "播完是否保持最后一帧" },
    loop: { type: "boolean", nullable: true, description: "是否循环" },
    time_scale: { type: "number", nullable: true, description: "速度倍率" },
    rad: { type: "number", nullable: true, description: "Z轴旋转(弧度)" },
    rotation: {
      type: "object", nullable: true, description: "旋转(弧度 x,y,z)",
      properties: {
        x: { type: "number" },
        y: { type: "number" },
        z: { type: "number" },
      },
    },
    scale: {
      type: "object", nullable: true, description: "缩放系数(x,y,z)",
      properties: {
        x: { type: "number" },
        y: { type: "number" },
        z: { type: "number" },
      },
    },
    offset: {
      type: "object", nullable: true, description: "平移偏移(x,y,z)",
      properties: {
        x: { type: "number" },
        y: { type: "number" },
        z: { type: "number" },
      },
    },
  },
});
